'use client';

import { useEffect, useRef } from 'react';
import { useAnimatedNavigate } from '@/hooks/useAnimatedNavigation';
import HeroSection from '@/sections/HeroSection';
import TextBenefitsSection from '@/sections/TextBenefitsSection';
import SlideImageSection from '@/sections/SlideImageSection';
import OverviewSection from '@/sections/OverviewSection';
import FAQSection from '@/sections/FAQSection';
import LocationSection from '@/sections/LocationSection';
import NavbarContainer from '@/components/client/NavbarContainer';
import FooterWrapper from '@/components/ui/FooterWrapper';
import { useLoading } from '@/contexts/LoadingContext';
import useAspectRatio from '@/hooks/useAspectRatio';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import {
  BranchSectionContent,
  BrandInfoSectionContent,
  CTASectionContent,
  FAQSectionContent,
  LanguageType,
  PageData,
  ServiceCategorySectionContent,
} from '@/lib/types';

import { debug } from '@/lib/debug';
import { setLanguage } from '@/lib/language';
gsap.registerPlugin(useGSAP, ScrollTrigger);

interface HomePageClientProps {
  pageData: PageData | null;
  initialLanguage: LanguageType;
}

export default function HomePageClient({ pageData, initialLanguage }: HomePageClientProps) {
  const language: LanguageType = initialLanguage;
  const aspectRatio = useAspectRatio();
  const { setAspectRatioReady } = useLoading();
  const animatedNavigate = useAnimatedNavigate();

  const slideImagePositionRef = useRef<number | null>(null);
  const locationPositionRef = useRef<number | null>(null);

  useEffect(() => {
    document.body.style.backgroundColor = '#FFF6E6';
    document.body.classList.add('will-change-bg');

    window.dispatchEvent(new CustomEvent('navigationTransitionReady'));
  }, []);

  // Notify loading system when aspect ratio is ready
  useEffect(() => {
    debug.log('[HomePageClient] aspectRatio changed:', aspectRatio);
    if (aspectRatio !== 0) {
      debug.log('[HomePageClient] aspectRatio ready, setting AspectRatioReady to true');
      setAspectRatioReady(true);
    }
  }, [aspectRatio, setAspectRatioReady]);

  const { contextSafe } = useGSAP({});

  // TODO: the color transition might get broken on resizes. check check check

  // Create a safe animation function that can be called anytime
  const animateBg = contextSafe(() => {

    // Kill only the color transition ScrollTriggers
    ScrollTrigger.getAll().forEach(trigger => {
      if (trigger.vars.id?.toString().startsWith('color-transition-')) {
        trigger.kill();
      }
    });
    if (slideImagePositionRef.current && locationPositionRef.current) {

      const sections: {
        startY: number;
        fromColor: string;
        toColor: string;
      }[] = [];
      sections.push({
        startY: slideImagePositionRef.current,
        fromColor: '#FFF6E6', // TODO: use variables
        toColor: '#F7E8D7',
      });
      sections.push({
        startY: locationPositionRef.current,
        fromColor: '#F7E8D7', // TODO: use variables
        toColor: '#FFF6E6', // TODO: use variables
      });

      sections.forEach((section, index) => {
        ScrollTrigger.create({
          start: `top+=${section.startY} center`,
          // markers: true,
          id: `color-transition-${index}`,
          onEnter: () => {
            gsap.to(document.body, {
              backgroundColor: section.toColor,
              duration: 1,
              ease: "power2.inOut"
            });
          },
          onLeaveBack: () => {
            gsap.to(document.body, {
              backgroundColor: section.fromColor,
              duration: 1,
              ease: "power2.inOut"
            });
          }
        });
      });
    }

  });

  if (!pageData) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  const faqSectionPageData: FAQSectionContent = {
    questions: pageData.sections.find(section => section.type === 'faq')?.content
  } as FAQSectionContent;

  const branchInfoSectionPageDataRaw: BranchSectionContent = {
    branches: pageData.sections.find(section => section.type === 'branch')?.content || []
  } as BranchSectionContent;

  const branchInfoSectionPageData: BranchSectionContent = {
    branches: (branchInfoSectionPageDataRaw.branches || []).map(branch => ({
      ...branch,
      openingHoursSatToWed: language === 'arabic' ? '8 ص – 10 م' : '8 AM – 10 PM',
      openingHoursThu: language === 'arabic' ? '8 ص – 8 م' : '8 AM – 8 PM',
      openingHoursFri: language === 'arabic' ? '10 ص – 6:30 م' : '10 AM – 6:30 PM',
      branchAddress: language === 'arabic'
        ? 'لومير سنتر، حي حطين\nRRHA8736، الرياض، السعودية'
        : 'Lumière Center, Hittin\nRRHA8736, Riyadh, SA',
      timetableHeading: language === 'arabic' ? "ساعات العمل" : "Opening hours",
      locationHeading: language === 'arabic' ? "تجدوننا هنا" : "Find us here",
      buttonText: language === 'arabic' ? "انضم لقائمة الانتظار" : "Join our waitlist",
      satWedLabel: language === 'arabic' ? "السبت - الأربعاء" : "Sat – Wed",
      thursdayLabel: language === 'arabic' ? "الخميس" : "Thu",
      fridayLabel: language === 'arabic' ? "الجمعة" : "Fri",
      googleMapsButtonText: language === 'arabic' ? "توجّه الآن" : "Get directions"
    }))
  };

  const ctaSectionPageData: CTASectionContent = pageData.sections.find(section => section.type === 'call-to-action')?.content as CTASectionContent;
  const brandInfoSectionPageData: BrandInfoSectionContent = pageData.sections.find(section => section.type === 'brand-info')?.content as BrandInfoSectionContent;
  const serviceCategorySectionPageData: ServiceCategorySectionContent = { categories: pageData.sections.find(section => section.type === 'service-category')?.content } as ServiceCategorySectionContent;

  return (
    <>
      <NavbarContainer
        language={language}
        setLanguage={setLanguage}
        onJoinRiviveClick={() => animatedNavigate('/signup')}
      />
      <div className="flex flex-col items-center section-gaps relative">
        <HeroSection
          title={language === 'arabic' ? "تألق بجمالك الطبيعي" : "Embrace your natural beauty"}
          paragraph={language === 'arabic'
            ? "اكتشف ريڤيڤ، مركز التجميل الفاخر في الرياض، المتخصص في إبراز جمالك الطبيعي من خلال علاجات مخصصة وغير جراحية"
            : "Discover Rivive, Riyadh's luxury beauty center, dedicated to enhancing your natural beauty through personalized, non-invasive treatments."
          }
          hint={language === 'arabic' ? "احجز مكانك كعضو مؤسس" : "Secure your spot as a founding member"}
          buttonText={language === 'arabic' ? "انضم لقائمة الانتظار" : "Join our waitlist"}
          imageSrc="https://dependable-apparel-f6b4f02674.media.strapiapp.com/Sign_Up_Hero_c22f7e352f.webp"
          imageAlt="Mountain landscape"
          language={language}
          titleFont="small"
          useMuxPlayer={true}
          muxPlaybackIdDesktop="vZOqaovQI5BZlFH32gq005uTXadxpLOuvPrfmIM3ehH4"
          muxPlaybackIdMobile="RXEoHDE025FUL02nnD922ihtRPxWoQrILlu00SYC02ybiXw"
        />

        <TextBenefitsSection
          text={language === 'arabic' ? 'نحن نؤمن بأن الجمال الطبيعي هو أرقى أشكال الفخامة، واحتفاء أصيل بفرادتنا وتميّزنا.' : 'We believe that natural beauty is the ultimate luxury, an authentic celebration of our uniqueness.'}
          showLogo={true}
          language={language}
          benefits={language === 'arabic' ? [
            {
              title: 'عناية تلامس شخصيتك',
              content: 'استمتعوا بتجربة فاخرة تُعنى بالضيافة، حيث يكون جمالك وخصوصيتك ورفاهيتك دائمًا في المقام الأول'
            },
            {
              title: 'تقنية تصنع الفرق',
              content: 'اكتشفوا مستقبل الجمال مع أجهزة حصرية في المملكة العربية السعودية مصممة لتتكيّف بذكاء مع احتياجات جسدكم الفريدة.'
            },
            {
              title: 'جمال يدوم بأمان',
              content: 'اختبروا رحلة جمال متجددة بأساليب آمنة وغير جراحية، تضمن لكِ رفاهية تدوم'
            }
          ] : [
            {
              title: 'Care that feels personal',
              content: 'Enjoy a luxurious experience driven by hospitality, where your beauty, privacy and well-being always comes first.'
            },
            {
              title: 'Technology that transforms',
              content: 'Access the future of beauty through KSA-exclusive devices that intelligently adapt to your body\'s needs.'
            },
            {
              title: 'Safe, lasting beauty',
              content: 'Experience a transformative beauty journey through non-invasive methods that protect your lasting well-being.'
            }
          ]}
        />

        <SlideImageSection
          text={language === 'arabic' ? "انغمس في واحتنا المستوحاة من الطبيعة، حيث يشرق جمالك الحقيقي ويبدأ مشوارك نحو التميز." : "Step into our nature-inspired oasis, where your journey to authentic beauty awaits."}
          imageSrc="https://dependable-apparel-f6b4f02674.media.strapiapp.com/Index_Center_a521018ca9.webp"
          imageAlt="Mountain landscape"
          language={language}
          onInitialPosition={(pos) => {
            slideImagePositionRef.current = pos;
            animateBg();
          }}
          aspectRatio={aspectRatio}
        />

        <OverviewSection
          serviceCategorySectionPageData={serviceCategorySectionPageData}
          language={language}
          subheadingText={language === 'arabic' ? "خدماتنا" : "OUR SERVICES"}
          headingText={language === 'arabic' ? "تجارب مصممة خصيصًا لك" : "Treatments designed around you"}
          paragraphText={language === 'arabic' ? "استمتعوا بعلاجات مخصصة غير جراحية تمنحكم نتائج سريعة، محاطة بتجربة تجديدية فاخرة." : "Experience personalized, non-invasive protocols designed for efficient results, wrapped in a rejuvenating experience."}
        />

        <LocationSection
          branchInfoSectionPageData={branchInfoSectionPageData}
          language={language}
          onInitialPosition={(pos) => {
            locationPositionRef.current = pos;
            animateBg();
          }}
          subheadingText={language === 'arabic' ? "موقعنا" : "OUR LOCATION"}
          headingText={language === 'arabic' ? "زوروا واحتنا في قلب الرياض" : "Visit our oasis in the heart of Riyadh"}
        />

        <FAQSection
          language={language}
          questions={faqSectionPageData.questions || []}
          subheading={language === 'arabic' ? "الأسئلة الأكثر شيوعاً" : "ESSENTIAL FAQS"}
          heading={language === 'arabic' ? "معلومات أساسية لتجربتك" : "Essential information for your experience"}
          paragraph={language === 'arabic' ? "لقد أجبنا على أسئلتكم الأكثر شيوعًا لتتمكنوا من التخطيط لزيارتكم بسهولة. لم تجدوا ما تحتاجونه؟ فريقنا هنا لمساعدتكم، تواصلوا معنا في أي وقت." : "We've answered your most common questions so you can plan your visit with ease. Can't find what you need? Our team is here to help—reach out anytime."}
        />

        <FooterWrapper
          brandInfoSectionPageData={brandInfoSectionPageData}
          ctaSectionPageData={ctaSectionPageData}
          language={language}
          setLanguage={setLanguage}
        />
      </div>
    </>
  );
}
